import {
	ApolloClient,
	NormalizedCacheObject,
	InMemoryCache,
	createHttpLink,
	Observable,
	Operation,
	FetchResult,
	split
} from "@apollo/client";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { getMainDefinition } from "@apollo/client/utilities";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { RetryLink } from "@apollo/client/link/retry";

import { getAuth } from "firebase/auth";
import localStorageKeys from "../../constants/local-storage-keys";
import { isAnonymousRoute } from "../../routes/route-permissions";
import { buildLoginPath } from "../../utils/auth-redirect";
import { environment } from "../environment/environment";

const GQL_ENDPOINT = "http://localhost:8080/v1/graphql"; // LOCAL DEVELOPMENT
// const GQL_ENDPOINT = "https://staging.highnotes.com:8443/v1/graphql"; // STAGING
// const GQL_ENDPOINT = "https://prod.highnotes.com:8443/v1/graphql"; // PRODUCTION

/**
 * Waits for Firebase authentication to initialize
 * @returns Promise<void>
 */
const waitForAuthInit = async (): Promise<void> => {
	const auth = getAuth();
	if (auth.currentUser) return;

	return new Promise(resolve => {
		const unsubscribe = auth.onAuthStateChanged(user => {
			unsubscribe();
			resolve();
		});
	});
};

/**
 * Gets a fresh Firebase token with configurable options
 * @param options - Configuration object for token retrieval
 * @returns Promise<string | null> A promise that resolves to the new token or null if unable to get one
 */
type GetTokenOptions = {
	forceRefresh?: boolean; // Whether to force a new token from Firebase
	maxRetries?: number; // Maximum number of retry attempts
	retryDelay?: number; // Delay between retries in milliseconds
};

export const getFreshToken = async ({
	forceRefresh = false,
	maxRetries = 3,
	retryDelay = 1000
}: GetTokenOptions = {}): Promise<string | null> => {
	const auth = getAuth();

	// Helper function for retry logic
	const attemptGetToken = async (retriesLeft: number): Promise<string | null> => {
		try {
			// Wait for auth initialization on first try
			if (retriesLeft === maxRetries) {
				await waitForAuthInit();
			}

			const user = auth.currentUser;
			if (!user) {
				if (retriesLeft > 0) {
					// Wait a bit and retry
					await new Promise(resolve => setTimeout(resolve, retryDelay));
					return attemptGetToken(retriesLeft - 1);
				}
				console.warn("No user found after retries when attempting to refresh token");
				return null;
			}

			// Get token with force refresh parameter
			const token = await user.getIdToken(forceRefresh);
			window.localStorage.setItem(localStorageKeys.TOKEN_KEY, token);
			return token;
		} catch (error) {
			if (retriesLeft > 0) {
				console.debug(`Token retrieval attempt failed, ${retriesLeft} retries left`);
				await new Promise(resolve => setTimeout(resolve, retryDelay));
				return attemptGetToken(retriesLeft - 1);
			}
			console.error("Error refreshing Firebase token:", error);
			return null;
		}
	};

	return attemptGetToken(maxRetries);
};

// Check if any of our errors might be auth-related
const isAuthError = (error: any): boolean => {
	// Check the error structure specific to your case
	if (error?.extensions?.code === "validation-failed") {
		// Check if it's related to permissions/auth
		const errorPath = error?.extensions?.path || "";
		const errorMessage = error?.message || "";

		// Add specific checks for your case
		if (errorMessage.includes("field") && errorMessage.includes("not found")) {
			return true;
		}

		return errorPath.includes("selectionSet");
	}

	// Other auth error checks
	if (error.message?.includes("JWT")) return true;
	if (error.message?.includes("token")) return true;
	if (error.message?.includes("permission")) return true;
	if (error.extensions?.code === "UNAUTHENTICATED") return true;

	return false;
};

/**
 * Retries a failed operation with updated authorization headers
 * @param operation - The GraphQL operation that failed
 * @param token - The new authentication token
 * @returns The operation context with updated headers
 */
const updateOperationAuth = (operation: Operation, token: string) => {
	const context = operation.getContext();
	const oldHeaders = context.headers || {};

	// Update operation context with new authorization header
	operation.setContext({
		headers: {
			...oldHeaders,
			authorization: `Bearer ${token}`
		}
	});
};

/**
 * Custom fetch with longer timeout for slow connections
 */
const customFetch = (uri: RequestInfo | URL, options?: RequestInit) => {
  const body = options?.body as string;
  const isChatStream =
    body?.includes('startChatStream') ||
    body?.includes('StartChatStream') ||
    body?.includes('start_chat_stream');

  // Long-running Hasura actions: these run heavy server-side work (Doctrine +
  // Gemini) that can exceed the 60 s safety timeout. Aborting them client-side
  // leaves the action running on the server (creating the rows) while the
  // client sees a timeout and never gets the success response.
  // KEEP IN SYNC with the operation name + action root field — rename there, rename here.
  const isLongAction =
    body?.includes('createInspectionJob') ||
    body?.includes('create_inspection_job') ||
    body?.includes('createInstalledProduct') ||
    body?.includes('create_installed_product') ||
    body?.includes('extractPropertyFloorPlan') ||
    body?.includes('extract_property_floor_plan');

  // Streaming chat mutations must not be aborted mid-stream — Doctrine/Gemini
  // can take several minutes for large responses. Long actions are likewise
  // exempt from the abort. All other requests keep the 60 s safety timeout.
  if (isChatStream || isLongAction) {
    return fetch(uri, options);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s timeout

	return fetch(uri, { ...options, signal: controller.signal })
		.finally(() => clearTimeout(timeoutId))
		.catch(error => {
			if (error.name === "AbortError") {
				throw new Error("Request timeout");
			}
			throw error;
		});
};

// Global reference to network status handlers (will be set by context)
let networkStatusHandlers: {
	onOperationStart: (name: string) => void;
	onOperationSuccess: (name: string) => void;
	onOperationError: (name: string, error: any) => void;
} | null = null;

export const setNetworkStatusHandlers = (handlers: typeof networkStatusHandlers) => {
	networkStatusHandlers = handlers;
};

/**
 * Retry configuration for failed requests
 */
const retryLink = new RetryLink({
	delay: {
		initial: 300,
		max: Infinity,
		jitter: true
	},
	attempts: {
		max: 2, // Only retry twice to avoid long delays
		retryIf: (error, _operation) => {
			const operationName = _operation.operationName || "unknown";

      // Never retry mutations — they are not idempotent. A timed-out mutation
      // may already be running (and persisting rows) on the server, so a retry
      // would duplicate the server-side work (e.g. createInspectionJob /
      // createInstalledProduct creating duplicate records).
      const definition = getMainDefinition(_operation.query);
      const isMutation =
        definition.kind === "OperationDefinition" && definition.operation === "mutation";

      if (isMutation) return false;

      // Never retry streaming chat ops — doing so starts a second parallel
      // stream which causes duplicate responses and broken UI state.
      const isStreamingOp =
        operationName.toLowerCase().includes('chatstream') ||
        operationName.toLowerCase().includes('startchat');

      if (isStreamingOp) return false;

      const isRetryableError =
        !!error.networkError ||
        error.message?.includes("timeout") ||
        error.message?.includes("fetch");

			if (isRetryableError && networkStatusHandlers) {
				console.log(`Retrying ${operationName} due to network error`);
				networkStatusHandlers.onOperationStart(operationName);
			}

			return isRetryableError;
		}
	}
});

/**
 * Error handling link for Apollo Client that handles token refresh
 * This link intercepts GraphQL errors and attempts to refresh the token if
 * authentication errors are detected
 */

const errorLink = onError(
	({
		graphQLErrors, // GraphQL specific errors
		networkError, // Network errors
		operation, // The GraphQL operation that failed
		forward // A function to resend the operation
	}) => {
		const operationName = operation.operationName || "unknown";

		// First, check if we have any errors that need handling
		if (!graphQLErrors && !networkError) {
			// If no errors, mark operation as successful
			if (networkStatusHandlers) {
				networkStatusHandlers.onOperationSuccess(operationName);
			}
		}

		// Log all errors for debugging
		if (graphQLErrors) {
			graphQLErrors.forEach(error => {
				console.debug("GraphQL error:", {
					message: error.message,
					path: error.path,
					extensions: error.extensions
				});
			});
		}

		if (networkError) {
			console.debug("Network error in", operationName, ":", networkError.message);

			// Notify context about the error
			if (networkStatusHandlers) {
				networkStatusHandlers.onOperationError(operationName, {
					networkError,
					message: networkError.message
				});
			}

			// Try cache fallback for network issues
			const isNetworkIssue =
				networkError.message?.includes("timeout") ||
				networkError.message?.includes("fetch") ||
				networkError.message?.includes("CORS") ||
				networkError.name === "AbortError" ||
				(networkError as any).statusCode >= 500;

			if (isNetworkIssue) {
				console.log("Network issue in", operationName, "- attempting cache fallback");

				// Try to read from cache
				try {
					const client = (operation as any).getContext?.()?.client || (globalThis as any).__APOLLO_CLIENT__;

					if (client?.cache) {
						const cachedResult = client.readQuery({
							query: operation.query,
							variables: operation.variables,
							errorPolicy: "ignore"
						});

						if (cachedResult) {
							console.log("Using cached data for", operationName);
							return new Observable(observer => {
								observer.next({
									data: cachedResult,
									// Add flag to indicate this is cached data
									extensions: { fromCache: true }
								});
								observer.complete();
							});
						}
					}
				} catch (cacheError) {
					console.debug("No cache data available for", operationName);
				}
			}
		}

		console.log("GQL operation: ", operation.operationName);

		if (isAnonymousRoute(window.location.pathname)) return;

		const shouldHandleAuth =
			(networkError && isAuthError(networkError)) || (graphQLErrors && graphQLErrors.some(isAuthError));

		// Let other errors propagate normally
		if (!shouldHandleAuth) return;

		// Return a new Observable that will handle the token refresh
		return new Observable<FetchResult>(outerObserver => {
			let isCompleted = false; // Don't proceed if cancelled

			// Start the token refresh process to ensure we have all claims
			getFreshToken({ forceRefresh: true })
				.then(newToken => {
					if (isCompleted) return;

					if (!newToken) {
						window.localStorage.removeItem(localStorageKeys.TOKEN_KEY);

						// // If we couldn't get a new token, propagate an error, this trigger onError method of the mutation/query hook
						// outerObserver.error(
						//   new Error("Unable to refresh authentication token")
						// );
						// return;

						// Graceful completion instead of error propagation
						// Redirect to login, remembering the page so signing back in returns to it
						if (!isAnonymousRoute(window.location.pathname)) {
							window.dispatchEvent(
								new CustomEvent("authNavigation", {
									detail: { route: buildLoginPath(window.location) }
								})
							);
						}

						outerObserver.complete();
						return;
					}

					// Update the operation with the new token
					updateOperationAuth(operation, newToken);

					// Retry the operation with new token
					// The forward(operation) function returns another Observable. We need to “connect” these two Observables. That is:
					// The Observable we created to handle the token refresh: outerObserver.
					// The Observable that returns forward(operation), the original operation retry

					forward(operation).subscribe({
						next: value => {
							if (!isCompleted) {
								if (networkStatusHandlers) {
									networkStatusHandlers.onOperationSuccess(operationName);
								}
								outerObserver.next(value);
							}
						},
						error: error => {
							if (!isCompleted) {
								// If we still get an error after refresh, complete the operation
								console.error("Operation failed after token refresh:", error);
								if (networkStatusHandlers) {
									networkStatusHandlers.onOperationError(operationName, error);
								}
								outerObserver.complete();
							}

							// outerObserver.error(error)
						},
						complete: () => outerObserver.complete()
					});
				})
				.catch(error => {
					if (!isCompleted) {
						console.error("Token refresh failed:", error);
						outerObserver.complete(); // Complete instead of error

						// // If token refresh fails, propagate the error
						// outerObserver.error(
						//   new Error(`Token refresh failed: ${error.message}`)
						// );
					}
				});

			// Return cleanup function. This function is called when the Observable is unsubscribed
			// Add any cleanup logic here (e.g., abort controller for fetch calls)
			return () => {
				isCompleted = true;
				console.log("Token refresh operation cancelled");
			};
		});
	}
);

/**
 * Request link to track operation start
 */
const requestLink = setContext((operation, { headers }) => {
	const operationName = operation.operationName || "unknown";

	// Track operation start
	if (networkStatusHandlers) {
		networkStatusHandlers.onOperationStart(operationName);
	}

	return { headers };
});

// Auth link that checks token before each request
const authLink = setContext(async (_, { headers }) => {
	const token = window.localStorage.getItem(localStorageKeys.TOKEN_KEY);
	if (isAnonymousRoute(window.location.pathname) && !token) {
		return { headers };
	}

	// If no token exists get a fresh one
	if (!token) {
		const newToken = await getFreshToken();
		return {
			headers: {
				...headers,
				authorization: newToken ? `Bearer ${newToken}` : null
			}
		};
	}

	return {
		headers: {
			...headers,
			authorization: token ? `Bearer ${token}` : null
		}
	};
});

let apolloClientInstance: ApolloClient<NormalizedCacheObject> | undefined;

export const getApolloClient = (refresh: boolean = false, token?: string) => {
	if (!refresh && apolloClientInstance) {
		return apolloClientInstance;
	}

	// Anonymous Client
	const httpLink = createHttpLink({
		uri: environment.gqlEndpoint,
		// uri: "http://localhost:8080/v1/graphql",
		fetch: customFetch, // Use custom fetch with timeout
		fetchOptions: {
			referrerPolicy: "unsafe-url"
		}
	});

	// Chain links: retry -> error -> request tracking -> auth -> http
	const link = retryLink.concat(errorLink).concat(requestLink).concat(authLink).concat(httpLink);

	const wsLink = new GraphQLWsLink(
		createClient({
			url: environment.gqlEndpointWebSocket,
			// Resolved on every connect, never captured once. The socket outlives the token
			// that opened it — it drops while the tab sleeps and reconnects hours later — and
			// Hasura closes an init carrying an expired token with a code graphql-ws treats as
			// fatal: every subscription on it then errors out for good and the screen stays
			// empty until the user reloads the browser.
			connectionParams: async () => {
				// Firebase hands back the cached token and mints a new one only when it is
				// spent, so this costs nothing on a healthy reconnect. The token passed in (or
				// the stored one) covers the case where auth has not initialised yet.
				const freshToken =
					(await getFreshToken({ maxRetries: 1, retryDelay: 300 })) ??
					token ??
					window.localStorage.getItem(localStorageKeys.TOKEN_KEY);

				return {
					"Access-Control-Allow-Origin": "*", // Required for CORS support to work
					credentials: true,
					headers: {
						authorization: freshToken ? `Bearer ${freshToken}` : null
					}
				};
			},
			on: {
				connected: () => console.log("GraphQLWsLink connected"),
				// event is the raw CloseEvent (or, for some graphql-ws close paths, an
				// object shaped like one). code/reason say exactly why the server closed
				// it — logging only the word "closed" throws that away.
				closed: event => console.log("GraphQLWsLink closed", (event as CloseEvent)?.code, (event as CloseEvent)?.reason),
				error: error => console.log("GraphQLWsLink error", error)
			}
		})
	);

	const splitLink = split(
		({ query }) => {
			const definition = getMainDefinition(query);
			return definition.kind === "OperationDefinition" && definition.operation === "subscription";
		},
		wsLink,
		link
	);

	apolloClientInstance = new ApolloClient({
		link: splitLink,
		cache: new InMemoryCache(),
		defaultOptions: {
			watchQuery: {
				fetchPolicy: "cache-and-network", // Always try cache first
				nextFetchPolicy(
					currentFetchPolicy,
					{
						// Either "after-fetch" or "variables-changed", indicating why the nextFetchPolicy function was invoked.
						reason,
						// The rest of the options (currentFetchPolicy === options.fetchPolicy).
						options,
						// The original value of options.fetchPolicy, before nextFetchPolicy was applied for the first time.
						initialFetchPolicy,
						// The ObservableQuery associated with this client.watchQuery call.
						observable
					}
				) {
					// When variables change, the default behavior is to reset options.fetchPolicy to context.initialFetchPolicy.
					// If you omit this logic, your nextFetchPolicy function can override this default behavior to prevent options.fetchPolicy from changing in this case.
					if (reason === "variables-changed") {
						return "network-only";
					}

					// If the network fails, keep the cache
					if (reason === "after-fetch") {
						return "cache-first";
					}

					if (currentFetchPolicy === "network-only") {
						return "network-only";
					}

					// Leave all other fetch policies unchanged.
					return currentFetchPolicy;
				},
				errorPolicy: "all", // Don't fail on GraphQL errors
				notifyOnNetworkStatusChange: true
			},
			query: {
				fetchPolicy: "network-only",
				errorPolicy: "all" // Don't treat GraphQL errors as fatal
			},
			mutate: {
				fetchPolicy: "no-cache",
				errorPolicy: "all"
			}
		}
	});

	// Store reference globally for error link access
	(globalThis as any).__APOLLO_CLIENT__ = apolloClientInstance;

	return apolloClientInstance;
};
